import { useContext, useEffect } from 'react';

import { createHealthieAccountChannelName } from '@/channels/utils/channelNames';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import { useChannelData } from '@/hooks/useChannel';
import PersonContext from '@/pages/Person/PersonContext';
import PersonRepository from '@/repositories/PersonRepository';
import Alert from '@/ui/components/Alert';

import { Props } from './types';

const CreateHealthieAccount = ({ personId, onError, onSuccess }: Props) => {
  const { addMessage } = useContext(AppContext);
  const { refetch: refetchPerson } = useContext(PersonContext);
  const personRepository = PersonRepository();

  const { isError, isSuccess } = useAPI({
    queryKey: ['createHealthieAccount', personId],
    queryFn: async () => {
      return await personRepository.createHealthieAccount(personId);
    }
  });

  useEffect(() => {
    if (isSuccess) {
      addMessage({ level: 'info', text: 'Successfully set task to create Healthie account' });
    }
  }, [addMessage, isSuccess]);

  useEffect(() => {
    if (isError) {
      onError?.();
    }
  }, [isError, onError]);

  const { channelData } = useChannelData<{ status: 'success' | 'failed'; errors?: string[] }>({
    channelId: createHealthieAccountChannelName(personId),
    channelName: 'Create Healthie Account'
  });

  useEffect(() => {
    const refetch = async () => {
      await refetchPerson();
    };

    if (channelData) {
      if (channelData.status === 'failed' && channelData.errors) {
        addMessage({
          level: 'error',
          text: `Something went wrong trying to create Healthie account: ${channelData.errors.join(', ')}`
        });
        onError?.();
      } else {
        void refetch();
        addMessage({ level: 'success', text: 'Successfully created Healthie account' });
        onSuccess?.();
      }
    }
  }, [addMessage, channelData, onError, onSuccess, refetchPerson]);

  return <Alert level={'info'} message="Creating Healthie Account..." />;
};

export default CreateHealthieAccount;
